import * as React from 'react';
import qs from 'query-string';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {
  HomepageCategoriesData,
  GraphQLData,
  Filter,
  Shop,
  PaginationState,
  SortOption,
} from 'interfaces';
import {Section} from 'components/UIKit';
import {Pagination} from 'components/Pagination';
import {CategoryCardLayout} from 'components/Marketplace';
import {getShops, GetShopsOptions, ShopsHistoryState} from 'utilities/shopsApi';
import {ShopFilters} from './components/ShopFilters';
import {ShopDisplay} from './components/ShopDisplay';
import * as locales from './locales';
import styles from './Shops.scss';

export interface ShopsProps {
  shops: Shop[];
  sortBy?: string;
  filters: Filter[];
  shopsPath: string;
  categoryId?: string;
  pagination: PaginationState;
  sortOptions: SortOption[];
  showFilters?: boolean;
  filtersInUrl?: boolean;
  showDisplayToggle?: boolean;
  shopsCountString: string;
  defaultSortOption: string;
  categories?: GraphQLData<HomepageCategoriesData>;
  categoriesPath?: string;

  // @TODO: Would love to change this naming/pattern
  representFilterStateInURL?: boolean;
}

export interface ShopsState {
  page: number;
  shops: Shop[];
  sortBy: string;
  pagination: PaginationState;
  filterState: Filter[];
  shopsLoading: boolean;
  shopsCountString: string;
  searchParamExisting: string;
}

function NOOP() {}

const UPDATE_ON_POPOVER_CLOSE = true;

function parseFilters(filters: Filter[]): Filter[] {
  return filters.filter((filter) => {
    if (!filter.options.length) {
      return false;
    }

    return true;
  });
}

class ShopsComponent extends React.Component<ShopsProps & WithI18nProps, ShopsState> {
  static displayName = 'Shops';
  pendingUpdate: boolean;
  activePopover: string;

  constructor(props) {
    super(props);

    this.pendingUpdate = false;
    this.activePopover = '';

    const {shops, sortBy, filters, pagination, shopsCountString, searchParamExisting} = props;

    const filterState = this.getParsedFilters(filters);

    this.state = {
      shops,
      sortBy: sortBy || this.props.defaultSortOption,
      page: pagination && pagination.currentPage,
      pagination,
      filterState,
      shopsLoading: false,
      shopsCountString,
      searchParamExisting,
    };
  }

  getParsedFilters = (filters) => {
    const {showFilters = true} = this.props;
    return showFilters ? parseFilters(filters) : [];
  };

  /**
   * Flush any pending API updates
   */
  clearPendingUpdates = () => {
    if (this.pendingUpdate) {
      this.getShopsFromApi();
      this.pendingUpdate = false;
    }
  };

  componentDidMount() {
    const queryString = qs.parse(location.search) || {};

    if (queryString && queryString.search) {
      this.setState({searchParamExisting: queryString.search});
    }

    window.addEventListener('popstate', this.handlePopstate);
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.handlePopstate);
  }

  /**
   * Handle popstate event,
   */
  handlePopstate = (event) => {
    // If popstate event has `state`, while there is nothing else pushing to history state we can assume it is our
    // ShopsHistoryState, so we serialize back to state
    if (event && event.state) {
      const {
        filters,
        sortBy,
        shops,
        pagination,
        shopsCountString,
      } = event.state as ShopsHistoryState;
      const page = event.state.page || pagination.currentPage;

      this.setState(
        {
          page,
          shops: shops || this.props.shops,
          sortBy: sortBy || this.props.defaultSortOption,
          pagination,
          filterState: this.getParsedFilters(filters),
          shopsCountString,
        },
        shops.length ? NOOP : this.getShopsFromApi,
      );
      return;
    }

    // If history state doesn't exist, we should reset to identity (state at page load)
    const {filters, sortBy, pagination, shops = [], shopsCountString} = this.props;

    this.setState({
      shops,
      page: 0,
      pagination,
      sortBy: sortBy || this.props.defaultSortOption,
      filterState: this.getParsedFilters(filters),
      shopsCountString,
    });
  };

  /**
   * Retrieve a set of listings from the API based on the state of the component.
   * Should buffer the update if there is an active popover and `UPDATE_ON_POPOVER_CLOSE` is set.
   * Results should be serialized back to component state.
   */
  getShopsFromApi = async () => {
    const {filterState: filters, sortBy, page, searchParamExisting} = this.state;
    const {categoryId, shopsPath: path, representFilterStateInURL: queryToUrl = false} = this.props;

    if (UPDATE_ON_POPOVER_CLOSE && this.activePopover) {
      this.pendingUpdate = true;
      return;
    }

    this.setState({shopsLoading: true});

    const apiParams: GetShopsOptions = {path, sortBy, filters, queryToUrl, categoryId};

    if (page) {
      apiParams.page = page;
    }

    if (searchParamExisting) {
      apiParams.search = searchParamExisting;
    }

    const shopsResponse = await getShops(apiParams);

    if (shopsResponse === null) {
      // @TODO: Error screen? Retry?
      this.setState({shopsLoading: false});
      return;
    }

    const {shops, pagination, filters: filterState, shopsCountString} = shopsResponse;
    this.setState({
      shops,
      pagination,
      filterState: this.getParsedFilters(filterState),
      shopsLoading: false,
      shopsCountString,
      searchParamExisting,
    });
  };

  /*
   * Reset page scroll to top after filter/page change
   */
  resetScroll = () => {
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    } else if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    } else {
      const html = document.querySelector('html');

      if (html) {
        html.scrollTop = 0;
      }
    }
  };

  /**
   * Handle filter updates from the `ShopFilters` component
   */
  handleFilterUpdate = (filterState) => {
    this.setState({filterState, page: 0}, this.getShopsFromApi);
  };

  /**
   * Handle page change from `Pagination` component
   */
  handlePageChange = (page) => {
    this.setState({page}, async () => {
      this.resetScroll();

      await this.getShopsFromApi();
      if ((window as any)._gaUTracker) {
        (window as any)._gaUTracker(
          '_exchange.send',
          'pageview',
          window.location.pathname + window.location.search,
        );
      }
    });
  };

  /**
   * Handle popover state updates from the ShopFilters component
   * @param filterType The open popover. If closed, will be empty string.
   */
  handlePopoverState = (filterType) => {
    this.activePopover = filterType;
    if (UPDATE_ON_POPOVER_CLOSE) {
      this.clearPendingUpdates();
    }
  };

  /**
   * Handle change events from FilterSort
   */
  handleSortChange = (sortBy) => this.setState({sortBy}, this.getShopsFromApi);

  render() {
    const {sortOptions, showFilters, categories, categoriesPath, i18n} = this.props;
    const {shops, sortBy, pagination, filterState, shopsLoading, shopsCountString} = this.state;
    const showPagination = Boolean(pagination);
    const homepageCategories =
      categories && categories.data ? categories.data.homepageCategories : [];
    const showCategories = homepageCategories && homepageCategories.length > 0;

    return (
      <>
        <Section spacing="medium" className={styles.Shops}>
          {showFilters && (
            <Section spacing="small" resetPaddingTop fullWidth>
              <ShopFilters
                sortBy={sortBy}
                filters={filterState}
                showSort
                disabled={shopsLoading}
                sortOptions={sortOptions}
                onSortChange={this.handleSortChange}
                onFilterUpdate={this.handleFilterUpdate}
                onPopoverChange={this.handlePopoverState}
              />
            </Section>
          )}
          <Section spacing="medium" resetPaddingTop fullWidth>
            <ShopDisplay shops={shops} loading={shopsLoading} countString={shopsCountString} />
            {showPagination && <Pagination onClick={this.handlePageChange} {...pagination} />}
          </Section>
        </Section>
        {showCategories && (
          <CategoryCardLayout
            heading={i18n.translate('categories.heading')}
            subhead={i18n.translate('categories.subhead')}
            categories={homepageCategories}
            ctaPath={categoriesPath}
            cta={i18n.translate('categories.cta')}
          />
        )}
      </>
    );
  }
}

export const Shops = withI18n({
  id: 'Shops',
  fallback: locales.en,
})(ShopsComponent);
